import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import HistoryIcon from "@mui/icons-material/History";
import { useTranslation } from "react-i18next";
import useAuth from "src/hooks/useAuth";
import { initiateSSOToApp } from "src/utils/sso";
import { openInMetaMask, needsMetaMaskBrowser } from "src/utils/metamaskDeepLink";
import {
  fetchConversationsList,
  markConversationsSeen,
  fetchConversationTranscript,
  fetchSuggestions,
  fetchVoiceCapability,
  sendArrivedEvent,
  sendSupportMessage,
  transcribeAudio,
} from "src/utils/supportClient";

/**
 * SupportChat — la conversazione con Jinkō sul pannello Network (stadio 2 del
 * design brief 16/08). Gemello di app/src/components/Support/SupportChat.js:
 * stessa UI e stesse chiavi di storage, cambiano i binari (react-router al
 * posto di next/navigation, i18next al posto di next-intl) e la direzione dei
 * bottoni cross-progetto (qui le route `app` passano da initiateSSOToApp).
 *
 * Mobile: fullscreen. Desktop: pannello fisso in basso a destra, 400×640.
 *
 * Stato: transcript e conversation_id in sessionStorage (per-tab, TTL 30'
 * allineato al server: oltre, il bot non ricorda il filo e si riparte puliti).
 * Il server lega comunque la conversazione allo userId del token.
 *
 * Rete: SOLO via supportClient (axiosInstance) — Authorization e refresh sono
 * dell'interceptor, qui non si tocca nessun token.
 */

const CONV_KEY = "support_conversation_id";
const TRANSCRIPT_KEY = "support_transcript";
const TRANSCRIPT_TTL_MS = 30 * 60 * 1000;
// Flag "accompagnamento in corso": scritto quando tocchi un bottone del bot,
// letto dal FAB per riaprire la chat da sola quando arrivi sulla pagina.
const GUIDE_KEY = "support_guide_pending";
// Flag "filo adottato dall'altra area": scritto dal FAB quando arrivi con
// ?support_conv nel link, consumato all'apertura per mostrare l'avviso.
const RESUMED_KEY = "support_resumed";
// Timbro "risposta del team VISTA" per conversazione (round 17): il pallino
// del FAB si accende solo se l'ultima attività di un filo con risposta del
// team è più nuova del timbro. Lo scrive questo widget quando MOSTRA il filo;
// lo legge il polling del FAB. sessionStorage: a sessione nuova il pallino
// riparte acceso, coerente con l'adozione che riporta il filo.
// Dal 20/08 il timbro sta sul SERVER (`reply_seen` nella lista), non piu' in
// sessionStorage: e' una proprieta' dell'utente, non della scheda del browser.
// In sessionStorage non attraversava app.summus.life <-> network.summus.life,
// ne' le schede, ne' i dispositivi — e il pallino si riaccendeva su risposte
// gia' lette (segnalato da GP dal pannello, 20/08).
function markReplySeen(uuid) {
  if (!uuid) return;
  markConversationsSeen([uuid]);
}

function clearConversation() {
  try {
    sessionStorage.removeItem(TRANSCRIPT_KEY);
    sessionStorage.removeItem(CONV_KEY);
  } catch {}
}

function loadTranscript() {
  try {
    const raw = sessionStorage.getItem(TRANSCRIPT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const messages = Array.isArray(parsed) ? parsed : parsed.messages;
    const at = Array.isArray(parsed) ? 0 : parsed.at || 0;
    if (!Array.isArray(messages) || Date.now() - at > TRANSCRIPT_TTL_MS) {
      clearConversation();
      return [];
    }
    return messages;
  } catch {
    return [];
  }
}

function saveTranscript(messages) {
  try {
    sessionStorage.setItem(
      TRANSCRIPT_KEY,
      JSON.stringify({ at: Date.now(), messages: messages.slice(-60) })
    );
  } catch {
    // storage pieno/negato: la chat continua, semplicemente non si ricorda
  }
}

// Un messaggio del transcript del server, nella forma che il render capisce.
// 'human' (risposta concordata col team) diventa 'bot': resta nel DB come
// metadato, ma a schermo e' un normale messaggio di Jinko (round 13) — il
// render non ha un ramo 'human', quindi lasciarlo passare com'e' significa
// una riga INVISIBILE (successo davvero: filo riaperto dalla Cronologia
// senza la risposta del team, 19/08).
function fromServerMessage(m) {
  return {
    role: m.role === "user" ? "user" : m.role === "bot" || m.role === "human" ? "bot" : "system",
    text: m.text,
    actions: Array.isArray(m.actions) ? m.actions : [],
    ticketId: m.ticketId || null,
    // Orario del messaggio (round timestamps): dal DB per i fili ripresi, dal
    // dispositivo per i turni vivi. Assente sui messaggi vecchi = nessun orario.
    at: m.at || null,
  };
}

// L'orario si mostra nel FUSO DEL DISPOSITIVO (standard dei messaggi: ognuno
// vede la sua ora); in memoria resta l'ISO UTC. Un `at` assente o rotto = ''.
function timeLabel(at, lang) {
  if (!at) return "";
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(lang === "en" ? "en-GB" : "it-IT", { hour: "2-digit", minute: "2-digit" });
}

// Etichetta del separatore di giorno: Oggi / Ieri / data estesa.
function dayLabel(at, lang, t) {
  const d = new Date(at);
  const oggi = new Date();
  const ieri = new Date(oggi);
  ieri.setDate(oggi.getDate() - 1);
  const stesso = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (stesso(d, oggi)) return t("support.today");
  if (stesso(d, ieri)) return t("support.yesterday");
  return d.toLocaleDateString(lang === "en" ? "en-GB" : "it-IT", { day: "numeric", month: "long" });
}

// Il giorno cambia tra due messaggi? (solo per chi ha l'orario)
function isNewDay(prevAt, at) {
  if (!at) return false;
  if (!prevAt) return true;
  const a = new Date(prevAt);
  const b = new Date(at);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return false;
  return a.getFullYear() !== b.getFullYear() || a.getMonth() !== b.getMonth() || a.getDate() !== b.getDate();
}

// Markdown-lite: il modello scrive **grassetti** e a-capo; i link inline
// vengono ridotti alla sola label (la navigazione vera passa dai bottoni
// `actions`, validati dal server contro routes.yaml).
function renderBotText(text) {
  const plain = String(text || "").replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1");
  return plain.split("\n").map((line, i) => (
    <span key={i}>
      {i > 0 && <br />}
      {line
        .split(/\*\*([^*]+)\*\*/g)
        .map((part, j) => (j % 2 === 1 ? <strong key={j}>{part}</strong> : part))}
    </span>
  ));
}

function TypingDots() {
  return (
    <Box sx={{ display: "flex", gap: 0.6, px: 2, py: 1.5, alignItems: "center" }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.45)",
            animation: "jinko-typing 1.2s ease-in-out infinite",
            animationDelay: `${i * 0.18}s`,
            "@keyframes jinko-typing": {
              "0%, 60%, 100%": { opacity: 0.3, transform: "translateY(0)" },
              "30%": { opacity: 1, transform: "translateY(-3px)" },
            },
          }}
        />
      ))}
    </Box>
  );
}

export default function SupportChat({ open, onClose, arrivalNonce = 0 }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  // Nome per il benvenuto: stessa catena della navbar (NavbarAccount) —
  // first_name vive in user_profile, non alla radice dell'oggetto user.
  const firstName =
    (user?.user_profile?.first_name || "").trim().split(/\s+/)[0] || null;
  // it resta it; ogni altra lingua del pannello (en/es/pt) va in inglese,
  // stessa mappatura del cambio lingua verso il backend (useLocales).
  const lang = String(i18n.language || "en").toLowerCase().startsWith("it")
    ? "it"
    : "en";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [messages, setMessages] = useState(loadTranscript);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  // Turno auto dell'accompagnamento in volo: la chat mostra i puntini anche
  // se l'utente non ha scritto nulla (round 10 — riaprendo subito dopo il tap
  // sembrava non fosse successo niente, mentre il bot stava rispondendo).
  const [autoPending, setAutoPending] = useState(false);
  // Suggerimenti contestuali (round 11): arrivano dal servizio in base alla
  // pagina; finche' non rispondono restano i tre di ripiego dell'i18n, cosi'
  // la chat non e' mai nuda.
  const [suggestions, setSuggestions] = useState(null);
  const [blocked, setBlocked] = useState(null);
  // Cronologia (stadio 3-B): lista delle proprie conversazioni dallo store
  // DB. storeEnabled=false (store spento o irraggiungibile) = la voce non
  // esiste, il widget si comporta come prima.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  // Fili con risposta del team NON ancora vista al momento dell'apertura
  // della lista: in questa visita portano il chip «Risposta nuova».
  const [freshReplies, setFreshReplies] = useState(() => new Set());
  const [storeEnabled, setStoreEnabled] = useState(false);
  // Dettatura vocale: MediaRecorder → trascrizione server-side → testo nel
  // campo input, che l'utente rivede PRIMA di inviare (la voce non manda da sola).
  const [voiceAvailable, setVoiceAvailable] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // Specchio di `messages` per gli effetti su [open] (vedi effetto persisti).
  const messagesRef = useRef(messages);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    const canRecord =
      typeof window !== "undefined" &&
      navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== "undefined";
    if (!canRecord) return;
    fetchVoiceCapability().then((v) => {
      if (alive) setVoiceAvailable(v);
    });
    return () => {
      alive = false;
    };
  }, [open]);

  const stopRecording = () => {
    recorderRef.current?.stop();
    recorderRef.current?.stream?.getTracks().forEach((tr) => tr.stop());
    setRecording(false);
  };

  const startRecording = async () => {
    if (recording || transcribing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      // Niente mimeType forzato: iOS registra audio/mp4, Chrome audio/webm.
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (!blob.size) return;
        setTranscribing(true);
        try {
          const text = await transcribeAudio(blob, lang);
          if (text) setInput((v) => (v ? v + " " : "") + text);
        } catch (err) {
          // 429 = tetto giornaliero delle dettature (3-E): messaggio suo,
          // altrimenti sembra un guasto e l'utente riprova a vuoto.
          // I due interceptor rigettano cose diverse: l'app rilancia l'errore
          // intero, il pannello solo il BODY (utils/axios.js). Si guardano
          // entrambe le forme, altrimenti qui il ramo non scatta mai
          // (finding risk-reviewer 17/08).
          const limite =
            err?.response?.status === 429 ||
            err?.response?.data?.error === "transcribe_limit" ||
            err?.error === "transcribe_limit";
          setMessages((m) => [
            ...m,
            { role: "system", text: limite ? t("support.voiceLimit") : t("support.voiceFailed") },
          ]);
        } finally {
          setTranscribing(false);
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      setMessages((m) => [...m, { role: "system", text: t("support.voiceDenied") }]);
    }
  };

  // Filo adottato dall'altra area (flag scritto dal FAB all'arrivo col
  // ?support_conv): la CRONOLOGIA la serve il server (/api/conversation) —
  // la sessionStorage qui non ce l'ha, i domini sono separati. Se la
  // conversazione è scaduta (TTL 30') resta il solo avviso di ripresa.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    try {
      if (sessionStorage.getItem(RESUMED_KEY) !== "1") return;
      sessionStorage.removeItem(RESUMED_KEY);
      const convId = sessionStorage.getItem(CONV_KEY);
      setMessages([{ role: "system", text: t("support.resumed") }]);
      fetchConversationTranscript(convId).then((history) => {
        if (!alive || !history.length) return;
        setMessages([...history.map(fromServerMessage), { role: "system", text: t("support.resumed") }]);
      });
    } catch {}
    return () => {
      alive = false;
    };
  }, [open, t]);

  // Suggerimenti per la pagina corrente: si chiedono all'apertura (e se la
  // pagina cambia mentre la chat e' aperta).
  useEffect(() => {
    if (!open) return;
    let alive = true;
    fetchSuggestions({ route: pathname, lang }).then((list) => {
      if (alive && list.length) setSuggestions(list);
    });
    return () => {
      alive = false;
    };
  }, [open, pathname, lang]);

  // ALLINEAMENTO COL SERVER all'apertura (round 14). Il difetto che ha rotto
  // il giro dei ticket: la risposta concordata col team viene scritta nel
  // filo dal SERVIZIO (fuori da questa tab), ma il widget mostrava la sua
  // copia locale e non chiedeva mai se fosse arrivato altro — cosi' il
  // messaggio esisteva in DB e l'utente non lo vedeva. Vale anche per il
  // secondo dispositivo. Si sostituisce solo se il server ne ha DI PIU':
  // cosi' un server che non risponde non cancella mai quello che si vede.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    let convId = null;
    try {
      convId = sessionStorage.getItem(CONV_KEY);
    } catch {}
    if (!convId) return;
    fetchConversationTranscript(convId).then((remoti) => {
      if (!alive || !remoti.length) return;
      setMessages((locali) => {
        const contaVisibili = (l) => l.filter((m) => m.role === "user" || m.role === "bot").length;
        const n = contaVisibili(locali);
        if (remoti.length <= n) return locali;
        const righe = remoti.map(fromServerMessage);
        // Spartiacque ANCHE nel filo vivo (richiesta GP 19/08): se il blocco
        // arrivato mentre non guardavi contiene risposte del team, la riga
        // «Ci sono novità dal team» si inserisce dove inizia il nuovo — coi
        // soli timestamp, due messaggi dello stesso giorno resterebbero un
        // muro unico. Solo se il filo aveva già contenuto (n > 0): a chat
        // vuota ci pensano adozione e ripresa cross-area, con le loro righe.
        if (n > 0 && remoti.slice(n).some((m) => m.role === "human")) {
          righe.splice(n, 0, { role: "system", text: t("support.teamFollowup") });
        }
        return righe;
      });
    });
    return () => {
      alive = false;
    };
  }, [open, t]);

  // CIÒ CHE VEDI IN CRONOLOGIA È VISTO (fix pallino sempre acceso, 19/08):
  // con più fili in stato «Risposta dal team», il timbro veniva scritto solo
  // sul filo aperto e gli altri tenevano il pallino acceso per sempre. Aprire
  // la lista mette i chip a schermo: il segnale è consegnato, si timbrano
  // tutti i fili in quello stato. Un filo NUOVO (o attività nuova) riaccende
  // comunque: il timbro è per last_activity_at, non per sempre.
  // PRIMA di timbrare si fotografa cosa era ancora non-visto (richiesta GP
  // 19/08): in QUESTA visita quei fili portano il chip «Risposta nuova», così
  // capisci subito quale aprire — con un filo solo come con dieci. Alla
  // visita dopo tornano al normale «Risposta dal team» finché non li chiudi.
  useEffect(() => {
    if (!historyOpen) return;
    const conRisposta = (historyItems || []).filter(
      (c) => c.status === "open" && c.last_role === "human"
    );
    setFreshReplies(new Set(conRisposta.filter((c) => !c.reply_seen).map((c) => c.uuid)));
    // Un solo giro per tutti i fili mostrati, invece di N chiamate.
    markConversationsSeen(conRisposta.map((c) => c.uuid));
  }, [historyOpen, historyItems]);

  // Cronologia conversazioni (3-B): si carica all'apertura del widget. Se lo
  // store non e' attivo la voce non compare e nulla cambia.
  //
  // ADOZIONE del filo con risposta del team (round 16). Il caso reale
  // (18/08): la risposta concordata arriva ORE dopo, il transcript locale e'
  // scaduto (30') e la scadenza cancella anche CONV_KEY — quindi
  // l'allineamento non chiede piu' nulla al server e la risposta, GIA'
  // consegnata in DB, resta invisibile finche' l'utente non scava nella
  // Cronologia. A chat vuota, se la lista (che qui abbiamo appena scaricato,
  // e il server la calcola DOPO la consegna) contiene un filo aperto con
  // ultimo messaggio del team, lo si adotta e lo si mostra. La partenza
  // pulita (decisione 15/08) resta per ogni altro caso, e il job delle 72h
  // che chiude i fili inattivi mette un confine al riaffiorare.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    fetchConversationsList().then(async ({ store, conversations }) => {
      if (!alive) return;
      setStoreEnabled(store);
      setHistoryItems(conversations);
      // Il filo che stai guardando è VISTO (round 17): timbro per il pallino
      // del FAB — l'allineamento in parallelo sta già portando in chat le
      // eventuali novità di questo filo.
      let attivo = null;
      try {
        attivo = sessionStorage.getItem(CONV_KEY);
      } catch {
        return;
      }
      if (attivo) {
        const c = (conversations || []).find((x) => x.uuid === attivo);
        if (c) markReplySeen(c.uuid);
        return; // filo attivo: ci pensa l'allineamento
      }
      if (messagesRef.current.some((m) => m.role === "user" || m.role === "bot")) return;
      // Si adotta solo una risposta NON ancora vista (fix 19/08 sera): senza
      // il timbro, dopo «Nuova» la chat riagganciava subito il filo appena
      // abbandonato, finché non veniva risolto. Stessa semantica del pallino.
      const attesa = (conversations || []).find(
        (c) => c.status === "open" && c.last_role === "human" && !c.reply_seen
      );
      if (!attesa) return;
      const history = await fetchConversationTranscript(attesa.uuid);
      if (!alive || !history.length) return;
      // Ricontrollo dopo l'attesa: se nel frattempo l'utente ha iniziato a
      // scrivere, la sua chat nuova vince.
      if (messagesRef.current.some((m) => m.role === "user" || m.role === "bot")) return;
      try {
        sessionStorage.setItem(CONV_KEY, attesa.uuid);
      } catch {}
      markReplySeen(attesa.uuid);
      // La riga annuncia le novita', quindi sta PRIMA del blocco di risposte
      // del team in fondo al filo (feedback GP 19/08) — non in coda, dove
      // arriva a notizia gia' letta.
      let taglio = history.length;
      while (taglio > 0 && history[taglio - 1].role === "human") taglio--;
      const righe = history.map(fromServerMessage);
      righe.splice(taglio, 0, { role: "system", text: t("support.teamFollowup") });
      setMessages(righe);
    });
    return () => {
      alive = false;
    };
  }, [open, t]);

  // Riprende una conversazione dallo storico: il transcript arriva dal
  // server (che oltre il TTL legge dallo store DB); scrivendo, il servizio
  // ricostruisce il contesto e una conversazione risolta torna aperta.
  const openFromHistory = async (c) => {
    setHistoryOpen(false);
    let current = null;
    try {
      current = sessionStorage.getItem(CONV_KEY);
    } catch {}
    if (current === c.uuid) return;
    try {
      sessionStorage.setItem(CONV_KEY, c.uuid);
    } catch {}
    markReplySeen(c.uuid);
    setBlocked(null);
    // Nessuna riga di sistema qui (feedback GP 19/08): chi tocca un filo
    // nella cronologia sa gia' cosa sta aprendo — "dall'altra area"
    // (support.resumed) resta solo per il ponte tra app e pannello.
    setMessages([]);
    const history = await fetchConversationTranscript(c.uuid);
    if (history.length) {
      setMessages(history.map(fromServerMessage));
    }
  };

  // ACCOMPAGNAMENTO (3-C): all'approdo sulla pagina attesa parte il turno
  // auto-innescato — in BACKGROUND, la chat non si apre da sola (decisione
  // 16/08: all'arrivo si vede la PAGINA, il pallino del FAB segnala). La
  // risposta del bot si accoda al transcript: aprendo la chat e' gia' li'.
  // `fired` prima della chiamata = max 1 tentativo per destinazione anche
  // lato client; i guardrail veri (guida attiva, route attesa, tetto 5,
  // limiti giornalieri, kill) stanno nel server, che altrimenti risponde
  // {skipped:true} e qui non succede niente.
  useEffect(() => {
    let alive = true;
    try {
      const raw = sessionStorage.getItem(GUIDE_KEY);
      if (!raw) return;
      const flag = JSON.parse(raw);
      if (flag.fired || Date.now() - (flag.at || 0) > 10 * 60 * 1000) return;
      if (String(flag.slug || "").split("?")[0] !== pathname) return;
      const convId = sessionStorage.getItem(CONV_KEY);
      if (!convId) return;
      sessionStorage.setItem(GUIDE_KEY, JSON.stringify({ ...flag, fired: true }));
      setAutoPending(true);
      sendArrivedEvent({ conversationId: convId, lang, route: pathname })
        .then((data) => {
          if (!alive) return;
          setAutoPending(false);
          if (!data || data.skipped || !data.reply) return;
          setMessages((m) => [
            ...m,
            { role: "system", text: t("support.guideArrived") },
            {
              role: "bot",
              text: data.reply,
              actions: Array.isArray(data.actions) ? data.actions : [],
              ticketId: data.escalated?.ticket_id || null,
              at: new Date().toISOString(),
            },
          ]);
          if (data.guide_next) {
            try {
              sessionStorage.setItem(GUIDE_KEY, JSON.stringify({ slug: data.guide_next, at: Date.now() }));
            } catch {}
          }
        })
        .catch(() => { if (alive) setAutoPending(false); }); // nessun retry: la guida prosegue a mano
    } catch {}
    return () => {
      alive = false;
    };
  }, [pathname, lang, t, arrivalNonce]);

  // Autoscroll in fondo a ogni messaggio nuovo (e all'apertura).
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, autoPending, open]);

  // La ref serve all'adozione del filo: negli effetti su [open] leggere
  // `messages` direttamente lo metterebbe nelle dipendenze (ri-esecuzione a
  // ogni messaggio), qui invece serve solo il valore corrente al momento giusto.
  useEffect(() => {
    saveTranscript(messages);
    messagesRef.current = messages;
  }, [messages]);

  const send = async (text) => {
    const message = text.trim();
    if (!message || sending || blocked) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: message, at: new Date().toISOString() }]);
    setSending(true);
    try {
      const conversationId = sessionStorage.getItem(CONV_KEY) || undefined;
      const data = await sendSupportMessage({
        message,
        conversationId,
        lang,
        route: pathname,
      });
      if (data.conversation_id) sessionStorage.setItem(CONV_KEY, data.conversation_id);
      // Guida attiva (3-C): il server annuncia la prossima destinazione
      // attesa — il flag arma pallino e evento di arrivo ANCHE se l'utente
      // ci andra' per conto suo, senza passare dal bottone.
      if (data.guide_next) {
        try {
          sessionStorage.setItem(GUIDE_KEY, JSON.stringify({ slug: data.guide_next, at: Date.now() }));
        } catch {}
      }
      setMessages((m) => [
        ...m,
        {
          role: "bot",
          text: data.reply,
          actions: Array.isArray(data.actions) ? data.actions : [],
          ticketId: data.escalated?.ticket_id || null,
          at: new Date().toISOString(),
        },
      ]);
      // Limite giornaliero o kill-switch: il testo di cortesia è nel reply,
      // qui si spegne solo l'input.
      if (data.blocked) setBlocked(data.blocked);
    } catch {
      // Rete giù, servizio spento, beta chiusa o 401 persistente del servizio:
      // bolla di sistema, input riutilizzabile. L'interceptor NON slogga il
      // pannello per gli errori di un host esterno (vedi utils/axios.js).
      setMessages((m) => [...m, { role: "system", text: t("support.unavailable") }]);
    } finally {
      setSending(false);
      if (!isMobile) inputRef.current?.focus();
    }
  };

  const onAction = (action) => {
    // Gli slug vengono da routes.yaml (owner-controlled), ma la cintura client
    // serve per il caso servizio compromesso: niente slug senza '/' iniziale,
    // e niente '//' — navigate("//evil.com") diventerebbe una navigazione
    // cross-origin piena (history fa fallback a location.assign).
    if (
      typeof action.slug !== "string" ||
      !action.slug.startsWith("/") ||
      action.slug.startsWith("//") ||
      action.slug.includes("\\") // il parser URL tratta \ come /: "/\evil.com" diventerebbe cross-origin
    )
      return;
    // PRIMA dello smistamento per progetto (finding risk-reviewer 23/08:
    // messo dopo, sulle destinazioni del pannello non scattava mai — e Jinkō
    // intanto prometteva a parole che si sarebbe continuato in MetaMask).
    // Percorso che finisce con una firma dal wallet, su un telefono dove il
    // wallet non è iniettato: la navigazione normale porterebbe l'utente fino
    // al pagamento per poi lasciarlo bloccato. Si apre invece il browser
    // dentro MetaMask sul primo passo. Indirizzo non assoluto (env mancante in
    // dev) = si ricade sulla navigazione normale.
    if (action.wallet && needsMetaMaskBrowser()) {
      const base =
        action.project === "app"
          ? (process.env.REACT_APP_APP_URL || "").replace(/\/$/, "")
          : window.location.origin;
      // NAVIGAZIONE, non finestra nuova (la scheda resterebbe vuota dopo il
      // passaggio a MetaMask — GP, 23/08).
      if (openInMetaMask(`${base}${action.slug}`)) return;
    }
    if (action.project === "network-frontend") {
      // Flag di accompagnamento SOLO per le navigazioni interne: la
      // sessionStorage non attraversa l'SSO verso l'app, e un flag orfano
      // riaprirebbe la chat al primo back nel pannello.
      try {
        sessionStorage.setItem(GUIDE_KEY, JSON.stringify({ slug: action.slug, at: Date.now() }));
      } catch {}
      onClose();
      navigate(action.slug);
      return;
    }
    if (action.project === "dapp") {
      // Sito NATURA (terramaternfts.com): destinazione ESTERNA su base FISSA
      // (mai da env: un env sbagliato diventerebbe un open redirect). Scheda
      // nuova, niente SSO e niente filo che segue: la' l'utente e' anonimo
      // per design e trova la Jinko pubblica del sito (decisione GP 22/08).
      window.open(`https://terramaternfts.com${action.slug}`, "_blank", "noopener");
      return;
    }
    // Route della Summus App: giro SSO esistente, destinazione inclusa.
    // Il filo segue l'utente: la conversazione viaggia nel link come
    // ?support_conv=<uuid> e il widget di destinazione la adotta. L'id e'
    // legato lato server allo userId del token: un id altrui viene ignorato.
    const appUrl = (process.env.REACT_APP_APP_URL || "http://app.summus_network.localhost").replace(/\/$/, "");
    const conv = sessionStorage.getItem(CONV_KEY);
    const sep = action.slug.includes("?") ? "&" : "?";
    initiateSSOToApp(`${appUrl}${action.slug}${conv ? `${sep}support_conv=${encodeURIComponent(conv)}` : ""}`);
  };

  const startNewConversation = () => {
    if (messages.length > 0 && !window.confirm(t("support.newConversationConfirm"))) return;
    clearConversation();
    // "Nuova" dalla vista Cronologia lasciava la lista a schermo con la chat
    // svuotata dietro (bug beta round 8): una nuova conversazione porta
    // SEMPRE alla chat.
    setHistoryOpen(false);
    setMessages([]);
    setBlocked(null);
    setInput("");
  };

  const empty = messages.length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      hideBackdrop={!isMobile}
      PaperProps={{
        sx: {
          bgcolor: "#0f0a1a",
          backgroundImage: "none",
          display: "flex",
          flexDirection: "column",
          ...(isMobile
            ? {}
            : {
                position: "fixed",
                bottom: 24,
                right: 24,
                m: 0,
                width: 400,
                height: "min(640px, calc(100vh - 48px))",
                borderRadius: "20px",
                border: "1px solid rgba(168, 85, 247, 0.3)",
              }),
        },
      }}
    >
      {/* Header. Sottotitolo su riga propria a tutta larghezza: nella riga
          del titolo, con Cronologia+Nuova, usciva sempre troncato (round 8b). */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* Il NOME non si comprime mai (round 9: "Jinkō" usciva "J." sul
            telefono): flexShrink 0 sul titolo + spacer flessibile; a stringersi
            sono i bottoni (padding/font compatti su xs), mai la parola. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
        {/* Il volto di Jinkō (round 11). L'anello sfumato resta: e' il segno
            grafico della bolla, la foto ci sta dentro. */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #a855f7 0%, #facc15 100%)",
            p: "2px",
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src="/jinko/avatar.webp"
            alt={t("support.avatarAlt")}
            sx={{ width: "100%", height: "100%", borderRadius: "50%", display: "block", objectFit: "cover" }}
          />
        </Box>
        <Typography sx={{ flexShrink: 0, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>
          Jinkō
        </Typography>
        <Box sx={{ flex: 1 }} />
        {storeEnabled && historyItems.length > 0 && (
          <Tooltip title={t("support.historyTitle")} arrow>
            <Button
              onClick={() => setHistoryOpen((v) => !v)}
              aria-label={t("support.historyTitle")}
              size="small"
              startIcon={<HistoryIcon sx={{ fontSize: "1rem" }} />}
              sx={{
                color: historyOpen ? "#a855f7" : "#e9d5ff",
                textTransform: "none",
                fontSize: { xs: "0.72rem", sm: "0.78rem" },
                border: "1px solid rgba(168, 85, 247, 0.35)",
                borderRadius: "10px",
                px: { xs: 0.9, sm: 1.2 },
                py: 0.3,
                minWidth: 0,
                "&:hover": { bgcolor: "rgba(168, 85, 247, 0.12)" },
              }}
            >
              {t("support.historyShort")}
            </Button>
          </Tooltip>
        )}
        {!empty && (
          <Tooltip title={t("support.newConversation")} arrow>
            <Button
              onClick={startNewConversation}
              size="small"
              startIcon={<RestartAltIcon sx={{ fontSize: "1rem" }} />}
              sx={{
                color: "#e9d5ff",
                textTransform: "none",
                fontSize: { xs: "0.72rem", sm: "0.78rem" },
                border: "1px solid rgba(168, 85, 247, 0.35)",
                borderRadius: "10px",
                px: { xs: 0.9, sm: 1.2 },
                py: 0.3,
                minWidth: 0,
                "&:hover": { bgcolor: "rgba(168, 85, 247, 0.12)" },
              }}
            >
              {t("support.newConversationShort")}
            </Button>
          </Tooltip>
        )}
        <IconButton onClick={onClose} aria-label={t("support.close")} sx={{ color: "rgba(255,255,255,0.6)" }}>
          <CloseIcon />
        </IconButton>
        </Box>
        <Typography variant="caption" noWrap sx={{ display: "block", mt: 0.5, color: "rgba(255,255,255,0.6)" }}>
          {t("support.subtitle")}
        </Typography>
      </Box>

      {/* Messaggi */}
      <Box
        ref={scrollRef}
        aria-live="polite"
        sx={{ flex: 1, overflowY: "auto", px: 2, py: 2, display: "flex", flexDirection: "column", gap: 1.25 }}
      >
        {historyOpen && (
          <>
            <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: 700, px: 0.5 }}>
              {t("support.historyTitle")}
            </Typography>
            {historyItems.map((c) => (
              <Box
                key={c.uuid}
                onClick={() => openFromHistory(c)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "14px",
                  border: "1px solid rgba(255,255,255,0.1)",
                  px: 2,
                  py: 1.25,
                  "&:hover": { bgcolor: "rgba(168, 85, 247, 0.08)" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    size="small"
                    label={
                      c.status === "resolved"
                        ? t("support.historyResolved")
                        : c.status === "closed"
                          ? t("support.historyClosed")
                          : // L'ULTIMA PAROLA DEL TEAM batte il ticket contabile
                            // (fix 19/08 sera): se il team ha risposto, la palla è
                            // all'utente — anche se il ticket risulta ancora open
                            // (tutti quelli pre-round-17 lo sono: `answered` si
                            // scrive solo alla consegna). «Ticket aperto» resta
                            // per la vera attesa: ultima parola del bot + ticket.
                            c.last_role === "human"
                            ? freshReplies.has(c.uuid)
                              ? t("support.historyNewReply")
                              : t("support.historyTeamReply")
                            : c.has_open_ticket
                              ? t("support.historyTicket")
                              : c.last_role === "bot"
                                ? t("support.historyWaiting")
                                : t("support.historyOpenState")
                    }
                    sx={
                      c.status === "resolved"
                        ? { bgcolor: "rgba(74, 222, 128, 0.12)", color: "#86efac", border: "1px solid rgba(74, 222, 128, 0.3)" }
                        : c.status === "closed"
                          ? { bgcolor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.15)" }
                          : c.last_role === "human"
                            ? freshReplies.has(c.uuid)
                              ? { bgcolor: "rgba(239, 68, 68, 0.16)", color: "#fecaca", border: "1px solid rgba(239, 68, 68, 0.5)", fontWeight: 700 }
                              : { bgcolor: "rgba(74, 222, 128, 0.14)", color: "#86efac", border: "1px solid rgba(74, 222, 128, 0.4)" }
                            : c.has_open_ticket
                              ? { bgcolor: "rgba(96, 165, 250, 0.14)", color: "#bfdbfe", border: "1px solid rgba(96, 165, 250, 0.4)" }
                              : c.last_role === "bot"
                                ? { bgcolor: "rgba(250, 204, 21, 0.12)", color: "#fde68a", border: "1px solid rgba(250, 204, 21, 0.35)" }
                                : { bgcolor: "rgba(168, 85, 247, 0.12)", color: "#e9d5ff", border: "1px solid rgba(168, 85, 247, 0.35)" }
                    }
                  />
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                    {new Date(c.last_activity_at || c.created_at).toLocaleString(lang === "en" ? "en-GB" : "it-IT", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Box>
                {c.summary && (
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mt: 0.5 }}>
                    {c.summary}
                  </Typography>
                )}
              </Box>
            ))}
          </>
        )}

        {!historyOpen && empty && (
          <>
            {/* Striscia di presentazione: c'e' SOLO prima del primo messaggio,
                poi lascia tutto lo spazio alla conversazione. La riga "AI di
                Summus" resta nell'header, sopra: con un volto fotorealistico
                la dichiarazione deve restare visibile. */}
            <Box
              component="img"
              src="/jinko/hero.webp"
              alt={t("support.avatarAlt")}
              sx={{
                width: "100%",
                borderRadius: "16px",
                display: "block",
                mb: 0.5,
                border: "1px solid rgba(168, 85, 247, 0.25)",
              }}
            />
            <Box
              sx={{
                alignSelf: "flex-start",
                maxWidth: "88%",
                bgcolor: "rgba(255,255,255,0.06)",
                borderRadius: "16px 16px 16px 4px",
                px: 2,
                py: 1.25,
              }}
            >
              <Typography variant="body2" sx={{ color: "#fff", lineHeight: 1.5 }}>
                {firstName
                  ? t("support.welcomeNamed", { name: firstName })
                  : t("support.welcome")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
              {(suggestions || ["s1", "s2", "s3"].map((k) => t(`support.suggestions.${k}`))).map((label) => (
                <Chip
                  key={label}
                  label={label}
                  onClick={() => send(label)}
                  sx={{
                    bgcolor: "rgba(168, 85, 247, 0.12)",
                    color: "#e9d5ff",
                    border: "1px solid rgba(168, 85, 247, 0.35)",
                    "&:hover": { bgcolor: "rgba(168, 85, 247, 0.22)" },
                  }}
                />
              ))}
            </Box>
          </>
        )}

        {!historyOpen && messages.map((msg, i) => (
          <Box key={i} sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {/* Separatore di giorno (round timestamps): compare quando la data
                cambia rispetto all'ultimo messaggio datato — è lui a spezzare
                il "muro" tra la cortesia di ieri e la risposta di oggi. */}
            {isNewDay(messages.slice(0, i).reverse().find((p) => p.at)?.at, msg.at) && (
              <Typography variant="caption" sx={{ alignSelf: "center", color: "rgba(255,255,255,0.45)", fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", fontSize: "0.68rem" }}>
                {dayLabel(msg.at, lang, t)}
              </Typography>
            )}
            {msg.role === "user" && (
              <Box sx={{ alignSelf: "flex-end", maxWidth: "88%" }}>
                <Box
                  sx={{
                    bgcolor: "#a855f7",
                    borderRadius: "16px 16px 4px 16px",
                    px: 2,
                    py: 1.25,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#fff", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                    {msg.text}
                  </Typography>
                </Box>
                {msg.at && (
                  <Typography variant="caption" sx={{ display: "block", textAlign: "right", color: "rgba(255,255,255,0.35)", mt: 0.25, fontSize: "0.66rem" }}>
                    {timeLabel(msg.at, lang)}
                  </Typography>
                )}
              </Box>
            )}
            {msg.role === "bot" && (
              <>
                <Box sx={{ alignSelf: "flex-start", maxWidth: "88%" }}>
                  <Box
                    sx={{
                      bgcolor: "rgba(255,255,255,0.06)",
                      borderRadius: "16px 16px 16px 4px",
                      px: 2,
                      py: 1.25,
                    }}
                  >
                    <Typography component="div" variant="body2" sx={{ color: "#fff", lineHeight: 1.55 }}>
                      {renderBotText(msg.text)}
                    </Typography>
                  </Box>
                  {msg.at && (
                    <Typography variant="caption" sx={{ display: "block", textAlign: "right", color: "rgba(255,255,255,0.35)", mt: 0.25, fontSize: "0.66rem" }}>
                      {timeLabel(msg.at, lang)}
                    </Typography>
                  )}
                </Box>
                {msg.actions?.map((a) => (
                  <Button
                    key={a.slug}
                    onClick={() => onAction(a)}
                    size="small"
                    variant="outlined"
                    sx={{
                      alignSelf: "flex-start",
                      borderColor: "rgba(168, 85, 247, 0.5)",
                      color: "#e9d5ff",
                      textTransform: "none",
                      borderRadius: "10px",
                      "&:hover": { borderColor: "#a855f7", bgcolor: "rgba(168, 85, 247, 0.1)" },
                    }}
                  >
                    {lang === "en" ? a.title_en : a.title_it} →
                  </Button>
                ))}
                {msg.ticketId && (
                  <Chip
                    icon={<ConfirmationNumberOutlinedIcon sx={{ fontSize: "1rem" }} />}
                    label={t("support.ticketOpened", { id: msg.ticketId })}
                    size="small"
                    sx={{
                      alignSelf: "flex-start",
                      maxWidth: "88%",
                      height: "auto",
                      py: 0.5,
                      bgcolor: "rgba(74, 222, 128, 0.12)",
                      color: "#86efac",
                      border: "1px solid rgba(74, 222, 128, 0.3)",
                      // Il testo del ticket e' lungo: senza questo veniva
                      // troncato con i puntini a meta' frase (feedback 18/08).
                      "& .MuiChip-label": { whiteSpace: "normal", overflow: "visible", textOverflow: "clip", lineHeight: 1.35 },
                    }}
                  />
                )}
              </>
            )}
            {msg.role === "system" && (
              <Typography
                variant="caption"
                sx={{ alignSelf: "center", color: "rgba(255,255,255,0.5)", textAlign: "center", px: 2 }}
              >
                {msg.text}
              </Typography>
            )}
          </Box>
        ))}

        {!historyOpen && (sending || autoPending) && (
          <Box sx={{ alignSelf: "flex-start", bgcolor: "rgba(255,255,255,0.06)", borderRadius: "16px 16px 16px 4px" }}>
            <TypingDots />
          </Box>
        )}
      </Box>

      {/* Input */}
      <Box
        sx={{
          display: historyOpen ? "none" : "flex",
          gap: 1,
          px: 2,
          py: 1.5,
          borderTop: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <TextField
          inputRef={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          placeholder={blocked ? t("support.inputDisabled") : t("support.placeholder")}
          disabled={Boolean(blocked)}
          multiline
          maxRows={4}
          fullWidth
          size="small"
          inputProps={{ maxLength: 4000, "aria-label": t("support.placeholder") }}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: "rgba(255,255,255,0.05)",
              borderRadius: "14px",
              color: "#fff",
              "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
              "&:hover fieldset": { borderColor: "rgba(168, 85, 247, 0.5)" },
              "&.Mui-focused fieldset": { borderColor: "#a855f7" },
            },
          }}
        />
        {voiceAvailable && (
          <IconButton
            onClick={() => (recording ? stopRecording() : startRecording())}
            disabled={Boolean(blocked) || transcribing}
            aria-label={recording ? t("support.voiceStop") : t("support.voiceStart")}
            sx={{
              alignSelf: "flex-end",
              width: 40,
              height: 40,
              color: recording ? "#fff" : "rgba(255,255,255,0.7)",
              bgcolor: recording ? "#dc2626" : "rgba(255,255,255,0.08)",
              "&:hover": { bgcolor: recording ? "#b91c1c" : "rgba(255,255,255,0.15)" },
              ...(recording && {
                animation: "jinko-rec 1.5s ease-in-out infinite",
                "@keyframes jinko-rec": {
                  "0%, 100%": { boxShadow: "0 0 0 0 rgba(220, 38, 38, 0.5)" },
                  "50%": { boxShadow: "0 0 0 8px rgba(220, 38, 38, 0)" },
                },
              }),
            }}
          >
            {recording ? <StopIcon sx={{ fontSize: "1.2rem" }} /> : <MicIcon sx={{ fontSize: "1.2rem" }} />}
          </IconButton>
        )}
        <IconButton
          onClick={() => send(input)}
          disabled={!input.trim() || sending || Boolean(blocked) || transcribing}
          aria-label={t("support.send")}
          sx={{
            alignSelf: "flex-end",
            bgcolor: "#a855f7",
            color: "#fff",
            width: 40,
            height: 40,
            "&:hover": { bgcolor: "#9333ea" },
            "&.Mui-disabled": { bgcolor: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.3)" },
          }}
        >
          <SendIcon sx={{ fontSize: "1.2rem" }} />
        </IconButton>
      </Box>
    </Dialog>
  );
}
